import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../Sidebar/Sidebar";
import {
  fetchWithTokenRefresh2,
  LARAVEL_BASE_URL,
  McpTokenGenerate_API,
  McpTokenList_API,
  McpTokenRevoke_API,
} from "../apiUtils";
import { getAccessToken } from "../../utils/SecurityService.js";
import loadingGIF from "../../assets/images/loading.gif";

const KEY_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6"/><path d="M15.5 7.5l3 3L22 7l-3-3"/>
  </svg>
);

const TRASH_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const COPY_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);

const MCP_URL = process.env.REACT_APP_MCP_URL ?? "https://lvstaging.ronspot.ie/mcp";

const COMMANDS = [
  {
    category: "📅 Bookings",
    items: [
      { label: "View upcoming bookings",   examples: ['"Show me my bookings this week"', '"What have I booked this week?"'],            desc: "Lists all your upcoming desk and parking reservations." },
      { label: "Book a single day",         examples: ['"Book me a desk tomorrow"', '"Book me next Monday"'],                           desc: "Books your preferred spot, or picks one for you." },
      { label: "Book a date range",         examples: ['"Book me every day next week"', '"Book me Monday to Friday"'],                   desc: "Books all weekdays in the range automatically." },
      { label: "Recurring pattern",         examples: ['"Book me every Monday in July"', '"Every Tuesday and Thursday for 6 weeks"'],    desc: "Picks only the days you specify across a longer range." },
      { label: "Cancel a booking",          examples: ['"Cancel my booking on Friday"', '"Cancel my desk on July 14th"'],               desc: "Removes an existing desk or parking reservation." },
      { label: "Booking history",           examples: ['"Show me my booking history"', '"What desk did I use last month?"'],             desc: "Shows your past bookings — default last 30 days." },
    ],
  },
  {
    category: "👥 Office presence & team",
    items: [
      { label: "Who's in the office",  examples: ['"Who\'s in the office today?"', '"Who\'s coming in on Friday?"'],              desc: "Shows everyone booked in your zone for that day." },
      { label: "Your team",             examples: ['"Show me my team members"', '"Who from my team is in on Wednesday?"'],         desc: "Filters office presence by your team only." },
    ],
  },
  {
    category: "🔍 Finding colleagues",
    items: [
      { label: "Find a colleague",       examples: ['"Find Jhanvi Shah\'s bookings"', '"When is Sarah coming in?"'],               desc: "Shows upcoming bookings. Type at least 3 characters." },
      { label: "Book near a colleague",  examples: ['"Book me near Jhanvi on Wednesday"', '"Spot close to John on Friday"'],       desc: "Finds the nearest available desks to your colleague's spot." },
    ],
  },
  {
    category: "🏢 Meeting rooms",
    items: [
      { label: "Check availability",  examples: ['"What meeting rooms are free Monday?"', '"Rooms for Wednesday afternoon?"'],          desc: "Lists available rooms with capacity and time slots." },
      { label: "Book a room",         examples: ['"Book a room Monday 2pm for 1 hour"', '"Room for Sprint Planning Wed 10–11:30"'],     desc: "Creates a timed reservation with an optional meeting name." },
      { label: "Cancel a meeting",    examples: ['"Cancel my meeting on Thursday"', '"Cancel the Sprint Planning booking"'],            desc: "Removes a meeting room reservation." },
    ],
  },
  {
    category: "⭐ Your usual spot",
    items: [
      { label: "View preference",       examples: ['"What\'s my usual spot?"', '"Show me my preferences"'],                       desc: "Claude remembers your last booked spot per zone automatically." },
      { label: "Set a preferred spot",  examples: ['"Set my usual desk to D-12"', '"Save NP-2 as my preferred parking"'],         desc: "Pins a spot so all future bookings in that zone use it." },
      { label: "Clear a preference",    examples: ['"Forget my usual desk"', '"Clear my parking preference"'],                    desc: "Removes the saved default — Claude picks next available." },
    ],
  },
  {
    category: "🔔 Other",
    items: [
      { label: "Spot availability",  examples: ['"What spots are free in the desk zone Monday?"'],                                desc: "Shows all available desks or parking in a zone." },
      { label: "Notifications",      examples: ['"Show me my Ronspot notifications"', '"Any updates on my bookings?"'],           desc: "Pulls your latest alerts and booking updates." },
      { label: "Profile & zones",    examples: ['"Show me my profile"', '"What zones do I have access to?"'],                    desc: "See which zones and spot types are available to you." },
      { label: "Help",               examples: ['"What can you do?"', '"Show me all Ronspot commands"'],                         desc: "Lists all available tools directly in Claude." },
    ],
  },
];

// ── Shared UI helpers ──────────────────────────────────────────────────────────

function CodeBlock({ lines }) {
  const [copied, setCopied] = useState(false);
  const text = lines.join("\n");
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ position: "relative" }}>
      <pre style={{
        background: "#f5f6f8", border: "1px solid #e0e3ea", borderRadius: 7,
        padding: "12px 14px", fontSize: 12, lineHeight: 1.7, margin: "8px 0",
        overflowX: "auto", color: "#1a1a1a",
      }}>{text}</pre>
      <button
        onClick={copy}
        style={{
          position: "absolute", top: 8, right: 8,
          background: copied ? "#198754" : "#fff",
          color: copied ? "#fff" : "#555",
          border: "1px solid #ddd", borderRadius: 5,
          padding: "2px 8px", fontSize: 11, cursor: "pointer",
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function StepList({ steps }) {
  return (
    <ol style={{ paddingLeft: 0, listStyle: "none", margin: "10px 0" }}>
      {steps.map((s, i) => (
        <li key={i} style={{ display: "flex", gap: 10, marginBottom: 9, alignItems: "flex-start" }}>
          <span style={{
            width: 22, height: 22, borderRadius: "50%",
            background: "#eef1fd", color: "#2E4BC6",
            fontSize: 11, fontWeight: 700, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
          }}>{i + 1}</span>
          <span style={{ fontSize: 13, color: "#333", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: s }} />
        </li>
      ))}
    </ol>
  );
}

function InfoBox({ children, type = "info" }) {
  const styles = {
    info:    { bg: "#eef1fd", border: "#2E4BC6", color: "#1e2a70" },
    warning: { bg: "#fff8e6", border: "#f59f00", color: "#7a5000" },
    success: { bg: "#f0fdf4", border: "#22c55e", color: "#166534" },
  };
  const s = styles[type];
  return (
    <div style={{
      background: s.bg, borderLeft: `3px solid ${s.border}`, color: s.color,
      borderRadius: "0 6px 6px 0", padding: "10px 14px",
      margin: "10px 0", fontSize: 13,
    }}>
      {children}
    </div>
  );
}

// ── OAuth connect banner (shown when ?connect=true is in URL) ──────────────────

function ConnectBanner({ onApprove, isConnecting, connectError }) {
  return (
    <div style={{
      background: "linear-gradient(135deg, #eef1fd 0%, #f0fdf4 100%)",
      border: "2px solid #2E4BC6", borderRadius: 12,
      padding: "24px 28px", marginBottom: 28,
    }}>
      {/* Title */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 28 }}>🔗</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#1e2a70" }}>
            Claude Desktop is requesting access
          </div>
          <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
            Click <strong>Connect</strong> to generate a secure token and link your account automatically. No copy-pasting needed.
          </div>
        </div>
      </div>

      {/* What happens */}
      <div style={{
        background: "#fff", borderRadius: 8, padding: "12px 16px",
        margin: "14px 0", fontSize: 12.5, color: "#333", lineHeight: 1.6,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#111" }}>What will happen:</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>✅ A new MCP token is generated for <strong>Claude Desktop</strong></div>
          <div>✅ The token is sent directly to Claude Desktop — you never see or copy it</div>
          <div>✅ You can revoke it here at any time</div>
        </div>
      </div>

      {connectError && (
        <div className="alert alert-danger py-2 small mb-3">{connectError}</div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button
          className="btn btn-primary px-4"
          style={{ fontWeight: 600 }}
          onClick={onApprove}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" /> Connecting…</>
          ) : (
            "Connect — allow Claude Desktop access"
          )}
        </button>
        <span style={{ fontSize: 12, color: "#888" }}>
          You are already signed in — no extra login needed.
        </span>
      </div>
    </div>
  );
}

// ── Guide tabs ─────────────────────────────────────────────────────────────────

function ClaudeWebGuide() {
  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="success">
        ✅ <strong>One-click setup.</strong> No token copying, no config files — just add the URL and log in.
      </InfoBox>

      <StepList steps={[
        'Open <a href="https://claude.ai" target="_blank" rel="noreferrer"><strong>claude.ai</strong></a> and sign in.',
        'Click your <strong>profile picture</strong> (bottom-left) → <strong>Settings</strong> → <strong>Connectors</strong> in the left sidebar (under "Customize").',
        'Click the <strong>Add</strong> button (top-right of the Connectors panel) → <strong>Add custom connector</strong>.',
        'Fill in the form:<br/><b>Name:</b> <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Ronspot</code>&nbsp;&nbsp;<b>Remote MCP server URL:</b> <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">' + MCP_URL + '</code><br/>Leave <b>OAuth Client ID</b> and <b>OAuth Client Secret</b> blank.',
        'Click <strong>Add</strong>. A new browser tab opens — log in to your Ronspot account if prompted.',
        'The <strong>"Claude Desktop is requesting access"</strong> banner appears at the top of this page. Click <strong>"Connect — allow Claude Desktop access"</strong>.',
        'Switch back to claude.ai — the connector shows as <strong>Connected ✅</strong>. Type <em>"What can you do with Ronspot?"</em> to verify.',
      ]} />

      <InfoBox type="info">
        ℹ️ The same steps work in the <strong>Claude Desktop</strong> app — open Settings → Connectors and follow the same flow.
      </InfoBox>
    </div>
  );
}

function ClaudeDesktopConfigGuide() {
  const config = JSON.stringify({
    mcpServers: {
      ronspot: {
        type: "streamable-http",
        url: MCP_URL,
        headers: { Authorization: "Bearer YOUR_TOKEN_HERE" },
      },
    },
  }, null, 2);

  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="warning">
        ⚠️ Use this only if the <strong>Claude.ai / Desktop (one-click)</strong> method above doesn't work for you.
      </InfoBox>

      <StepList steps={[
        'Generate an MCP token above and copy it — it\'s shown only once.',
        'Open the Claude Desktop config file:<br/><b>Mac:</b> <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-size:11px">~/Library/Application Support/Claude/claude_desktop_config.json</code><br/><b>Windows:</b> <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-size:11px">%APPDATA%\\Claude\\claude_desktop_config.json</code>',
        'Paste the config below, replacing <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">YOUR_TOKEN_HERE</code> with your token. If the file already has other servers, add the <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">"ronspot": { … }</code> block inside the existing <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">mcpServers</code> section.',
        '<strong>Save</strong> the file, then <strong>restart</strong> Claude Desktop.',
        'Open a new chat and type <em>"What can you do with Ronspot?"</em> to verify.',
      ]} />

      <p style={{ fontWeight: 600, margin: "12px 0 4px", color: "#111", fontSize: 12 }}>Config to paste:</p>
      <CodeBlock lines={config.split("\n")} />
    </div>
  );
}

function ClaudeCodeGuide({ token }) {
  const cmd = `claude mcp add ronspot ${MCP_URL} --transport http --header "Authorization: Bearer ${token || "YOUR_TOKEN_HERE"}"`;

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "flex-start" }}>
        <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#2E4BC6", color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>1</span>
        <div>
          <div style={{ fontWeight: 600, color: "#111", marginBottom: 4 }}>Install Claude Code</div>
          <div style={{ color: "#555", marginBottom: 6 }}>Claude Code is Anthropic's free AI assistant for the terminal.</div>
          <a
            href="https://claude.ai/download"
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#2E4BC6", color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: "none" }}
          >
            Download Claude →
          </a>
          <div style={{ fontSize: 11, color: "#888", marginTop: 5 }}>Already installed? Skip to step 2.</div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #eef1fd", margin: "4px 0 16px" }} />

      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "flex-start" }}>
        <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#2E4BC6", color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>2</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: "#111", marginBottom: 4 }}>Generate a token, then run this command</div>
          <div style={{ color: "#555", marginBottom: 8 }}>
            Open your terminal (<code style={{ background: "#f0f0f0", padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>Terminal</code> on Mac · <code style={{ background: "#f0f0f0", padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>PowerShell</code> on Windows) and paste:
          </div>
          {token ? (
            <InfoBox type="success">✅ Your token is pre-filled — just copy and run.</InfoBox>
          ) : (
            <InfoBox type="warning">⚠️ Generate a token first (top of this page), then come back — the command will be pre-filled.</InfoBox>
          )}
          <CodeBlock lines={[cmd]} />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #eef1fd", margin: "4px 0 16px" }} />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#2E4BC6", color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>3</span>
        <div>
          <div style={{ fontWeight: 600, color: "#111", marginBottom: 4 }}>Verify the connection</div>
          <div style={{ color: "#555", marginBottom: 8 }}>Start a new Claude session and type:</div>
          <div style={{ background: "#f5f6f8", border: "1px solid #e0e3ea", borderRadius: 7, padding: "10px 14px", fontStyle: "italic", fontSize: 13, color: "#111" }}>
            "What can you do with Ronspot?"
          </div>
          <InfoBox type="success">✅ Claude will list all available commands — you're connected.</InfoBox>
        </div>
      </div>
    </div>
  );
}

const SYSTEM_PROMPT_LINES = [
  "You are Ronspot Assistant — a workspace booking AI for Ronspot FlexWork.",
  "",
  "STARTUP",
  "Always call getProfile at the start of every conversation. Greet the user by first name: \"Hi [Name] 👋\" and state their default zone.",
  "",
  "FORMATTING — use markdown in every response:",
  "• ✅ confirmed/success   ❌ failed/cancelled   📅 date   🏢 desk   🚗 parking   🗓️ meeting room   👤 person   👥 group   🔔 notification",
  "• Dates: always write \"Monday 23 June\" — never ISO format (2025-06-23)",
  "• Bookings list: one line per booking → date | zone | spot",
  "• Confirmations: bold ✅ or ❌ header line, then details underneath",
  "• Office presence: numbered list with name + spot on each line",
  "• Availability: compact bullet list grouped by zone",
  "• Never truncate — always show all results",
  "",
  "BOOKINGS",
  "• makeBooking returns confirmed results — never say \"I'll try\" or \"this may take a moment\"",
  "• Recurring bookings: use start_date + end_date + days_of_week together",
  "• Mixed results: show ✅ per successful date and ❌ per failed date with the reason",
  "• Book near colleague: show the colleague's spot first, then the user's assigned spot",
  "",
  "CANCELLATIONS",
  "• Confirm cancellation with ❌ Cancelled — show the date and spot",
  "",
  "MEETING ROOMS",
  "• List rooms with name, capacity, and available time slots",
  "• Confirm with room name, date, start–end time, and meeting title if provided",
  "",
  "ERRORS",
  "• Show ❌ and the exact error message returned by the API",
  "• Always suggest a next step (check availability, try a different date, etc.)",
];

function CopilotStudioGuide({ token }) {
  const base    = MCP_URL.replace("/mcp", "");
  const specUrl = `${base}/openapi-copilot.json`;

  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="info">
        ℹ️ Microsoft Copilot Studio requires a <strong>Microsoft 365</strong> account.
        Once set up, the agent works in the browser and can be added to Teams, Outlook, and SharePoint.
      </InfoBox>

      <StepList steps={[
        'Generate an MCP token above (or use an existing one).',
        'Sign in to <a href="https://copilotstudio.microsoft.com" target="_blank" rel="noreferrer"><strong>copilotstudio.microsoft.com</strong></a> with your Microsoft 365 account.',
        'Click <strong>Create</strong> → <strong>New agent</strong>. Give it a name like <em>Ronspot Assistant</em> and a short description. Click <strong>Create</strong>.',
        'In the left sidebar, click <strong>Actions</strong> → <strong>+ Add an action</strong>.',
        'Choose <strong>New action</strong> → <strong>REST API</strong>.',
        'Click <strong>"Import from URL"</strong>, paste the schema URL below, then click <strong>Import</strong>.',
        'Under <strong>Authentication</strong>: set type to <strong>API key</strong>, location = <strong>Request header</strong>, header name = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Authorization</code>, key prefix = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Bearer</code>, then paste your MCP token as the key value.',
        'Click <strong>Save</strong> on the action. Repeat for any additional actions you want to enable.',
        'Go back to the agent overview → click <strong>Publish</strong> to make it available in Teams and Outlook.',
      ]} />

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Schema URL (paste into "Import from URL"):</p>
      <CodeBlock lines={[specUrl]} />

      {token && (
        <>
          <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Your MCP token (paste as the API Key value):</p>
          <CodeBlock lines={[token]} />
        </>
      )}

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Agent instructions (paste into the agent's Instructions field):</p>
      <CodeBlock lines={SYSTEM_PROMPT_LINES} />
    </div>
  );
}

function GeminiGuide({ token }) {
  const base    = MCP_URL.replace("/mcp", "");
  const specUrl = `${base}/openapi-gemini.json`;

  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="info">
        ℹ️ Google Gemini Gems require a <strong>Gemini Advanced</strong> subscription (Google One AI Premium or Google Workspace Business/Enterprise).
        Once created, the Gem is accessible at <a href="https://gemini.google.com" target="_blank" rel="noreferrer">gemini.google.com</a> and inside Gmail, Docs, and Drive.
      </InfoBox>

      <StepList steps={[
        'Generate an MCP token above (or use an existing one).',
        'Go to <a href="https://gemini.google.com/gems" target="_blank" rel="noreferrer"><strong>gemini.google.com/gems</strong></a> and click <strong>New Gem</strong>.',
        'Set the name to <em>Ronspot Assistant</em> and add a short description like "Book desks and check office presence."',
        'Under <strong>Instructions</strong>, paste the agent instructions below.',
        'Under <strong>Tools</strong> → click <strong>+ Add tool</strong> → choose <strong>API</strong>.',
        'Paste the schema URL below and click <strong>Import</strong>.',
        'Under <strong>Authentication</strong>: set type to <strong>API key</strong>, header = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Authorization</code>, prefix = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Bearer</code>, paste your MCP token.',
        'Click <strong>Save</strong> — your Gem appears in the left sidebar at gemini.google.com.',
      ]} />

      <InfoBox type="warning">
        ⚠️ If the <strong>Tools</strong> section is not visible, your Gemini plan may not yet support external API connections.
        Gemini for Workspace Enterprise enables this feature for all users in the organisation.
      </InfoBox>

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Schema URL (paste into "Import"):</p>
      <CodeBlock lines={[specUrl]} />

      {token && (
        <>
          <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Your MCP token (paste as the API Key value):</p>
          <CodeBlock lines={[token]} />
        </>
      )}

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Agent instructions (paste into the Instructions field):</p>
      <CodeBlock lines={SYSTEM_PROMPT_LINES} />
    </div>
  );
}

function SlackGuide({ token }) {
  const base = MCP_URL.replace("/mcp", "");

  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="info">
        ℹ️ Slack uses a different model to ChatGPT and Copilot — there is no OpenAPI import UI.
        Two options are available depending on your needs.
      </InfoBox>

      <div style={{ fontWeight: 700, fontSize: 12, color: "#2E4BC6", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, marginTop: 4 }}>
        Option A — Slack Workflow Builder (no code, basic actions)
      </div>
      <p style={{ color: "#555", marginBottom: 10 }}>
        Use Slack's built-in Workflow Builder to send HTTP requests to the Ronspot API. Good for automated daily presence reports or single-action shortcuts. Requires a <strong>Slack Pro or higher</strong> plan.
      </p>
      <StepList steps={[
        'In Slack, click <strong>More</strong> (left sidebar) → <strong>Automations</strong> → <strong>Workflow Builder</strong>.',
        'Click <strong>New Workflow</strong>. Choose a trigger — e.g. <em>Scheduled</em> (daily at 9am) or <em>Shortcut</em> (manually triggered).',
        'Add a step: <strong>HTTP request</strong>.',
        'Set the URL to any Ronspot API endpoint (e.g. <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">' + base + '/ai/v1/office</code>), method = <strong>GET</strong>.',
        'Under <strong>Headers</strong>, add: key = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Authorization</code>, value = <code style="background:#f0f0f0;padding:1px 5px;border-radius:3px">Bearer YOUR_TOKEN_HERE</code>.',
        'Add a <strong>Send a message</strong> step to post the response to a channel.',
        'Click <strong>Publish</strong>.',
      ]} />

      <div style={{ fontWeight: 700, fontSize: 12, color: "#2E4BC6", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, marginTop: 20 }}>
        Option B — Custom Slack App (full conversational experience)
      </div>
      <p style={{ color: "#555", marginBottom: 10 }}>
        For a full AI assistant experience in Slack (book desks, cancel, check presence by typing naturally), a small Slack Bolt app is needed. It receives messages, forwards them to the Ronspot API, and posts formatted replies. The same REST endpoints are used — no changes to the server.
      </p>

      <div style={{ background: "#f4f5fc", border: "1px solid #dce3ff", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, color: "#111", marginBottom: 6 }}>API base URL</div>
        <CodeBlock lines={[`${base}/ai/v1`]} />
        <div style={{ fontWeight: 600, color: "#111", margin: "10px 0 6px" }}>Authentication header</div>
        <CodeBlock lines={["Authorization: Bearer YOUR_MCP_TOKEN"]} />
        {token && (
          <>
            <div style={{ fontWeight: 600, color: "#111", margin: "10px 0 6px" }}>Your MCP token</div>
            <CodeBlock lines={[token]} />
          </>
        )}
      </div>

      <InfoBox type="info">
        ℹ️ A ready-to-deploy Slack Bolt app template for Ronspot is planned for a future release. For now, use the API details above to wire up your own Slack app.
      </InfoBox>
    </div>
  );
}

function ClaudeGuide({ token }) {
  const [sub, setSub] = useState("app");

  const subTabs = [
    { id: "app",  label: "Claude.ai / Desktop (one-click)" },
    { id: "cli",  label: "Config file" },
    { id: "code", label: "Claude Code (terminal)" },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            style={{
              fontSize: 12, padding: "4px 14px", borderRadius: 20, cursor: "pointer",
              border: "1px solid",
              borderColor: sub === t.id ? "#2E4BC6" : "#d0d5e8",
              background: sub === t.id ? "#2E4BC6" : "#f4f5fc",
              color: sub === t.id ? "#fff" : "#444",
              fontWeight: sub === t.id ? 600 : 400,
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "app"  && <ClaudeWebGuide />}
      {sub === "cli"  && <ClaudeDesktopConfigGuide />}
      {sub === "code" && <ClaudeCodeGuide token={token} />}
    </div>
  );
}

function ChatGPTGuide({ token }) {
  const base    = MCP_URL.replace("/mcp", "");
  const specUrl = `${base}/openapi-chatgpt.json`;

  return (
    <div style={{ fontSize: 13 }}>
      <InfoBox type="info">
        ℹ️ ChatGPT integration requires a <strong>Plus, Team, or Enterprise</strong> account.
        All booking logic runs server-side — ChatGPT gets clean responses without raw API calls.
      </InfoBox>

      <StepList steps={[
        'Generate an MCP token above (or use an existing one from the list below).',
        'Go to <a href="https://chat.openai.com/gpts/editor" target="_blank" rel="noreferrer">chat.openai.com/gpts/editor</a> and click the <strong>Configure</strong> tab.',
        'Set <strong>Name</strong> to <em>Ronspot Assistant</em> and add a short description like "Book desks and check office presence."',
        'Under <strong>Instructions</strong>, paste the system prompt below.',
        'Scroll to <strong>Actions</strong> → click <strong>"Create new action"</strong>.',
        'Under <strong>Authentication</strong>: set Type to <strong>API Key</strong>, Auth Type = <strong>Bearer</strong>, paste your MCP token.',
        'Click <strong>"Import from URL"</strong>, paste the schema URL below, then click <strong>Import</strong>.',
        'Click <strong>Save</strong> in the top-right — your Custom GPT is ready.',
      ]} />

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Schema URL (paste into "Import from URL"):</p>
      <CodeBlock lines={[specUrl]} />

      {token && (
        <>
          <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>Your MCP token (paste as the API Key):</p>
          <CodeBlock lines={[token]} />
        </>
      )}

      <p style={{ fontWeight: 600, margin: "14px 0 4px", color: "#111" }}>System prompt (paste into the Instructions field):</p>
      <CodeBlock lines={SYSTEM_PROMPT_LINES} />
    </div>
  );
}

function CommandsGuide() {
  return (
    <div style={{ fontSize: 13 }}>
      <p style={{ color: "#555", marginBottom: 14 }}>
        Type these naturally in Claude or ChatGPT — exact wording is not required.
      </p>
      {COMMANDS.map((section) => (
        <div key={section.category} style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#2E4BC6",
            textTransform: "uppercase", letterSpacing: "0.06em",
            borderBottom: "1px solid #dce3ff", paddingBottom: 5, marginBottom: 10,
          }}>
            {section.category}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {section.items.map((item) => (
              <div key={item.label} style={{
                background: "#fafbff", border: "1px solid #e2e6ff",
                borderRadius: 8, padding: "10px 13px",
              }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  {item.label}
                </div>
                {item.examples.map((ex, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: "#111", fontStyle: "italic", lineHeight: 1.45 }}>
                    {ex}
                  </div>
                ))}
                <div style={{ fontSize: 11.5, color: "#777", marginTop: 5, lineHeight: 1.4 }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{
        background: "#f0fdf4", border: "1px solid #bbf7d0",
        borderRadius: 8, padding: "14px 16px", marginTop: 8,
      }}>
        <div style={{ fontWeight: 700, color: "#166534", marginBottom: 8, fontSize: 13 }}>Tips</div>
        <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
          {[
            'Say dates naturally — "tomorrow", "next Friday", "July 14th", "end of this month".',
            "Claude remembers your last booked spot and reuses it automatically next time.",
            "For recurring bookings just describe the pattern — Claude handles the rest.",
            "Colleague search requires at least 3 characters — intentional for privacy.",
            "If bookings partially fail, Claude shows exactly which dates succeeded.",
          ].map((tip, i) => (
            <li key={i} style={{ display: "flex", gap: 8, marginBottom: 6, color: "#1a3a2a", fontSize: 12.5 }}>
              <span style={{ color: "#22c55e", fontWeight: 700, flexShrink: 0 }}>✓</span>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function McpUserGuide({ latestToken }) {
  const [tab, setTab] = useState("claude");

  const tabs = [
    { id: "claude",   label: "Claude" },
    { id: "chatgpt",  label: "ChatGPT" },
    { id: "copilot",  label: "Copilot Studio" },
    { id: "gemini",   label: "Google Gemini" },
    { id: "slack",    label: "Slack" },
    { id: "commands", label: "Commands & Examples" },
  ];

  return (
    <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid #e9ecef" }}>
      <h6 className="fw-semibold mb-1" style={{ color: "#111" }}>How to connect &amp; use</h6>
      <p className="text-muted small mb-3">Step-by-step setup and all available commands.</p>

      <ul className="nav nav-tabs mb-0" style={{ borderBottom: "1px solid #dee2e6", flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <li className="nav-item" key={t.id}>
            <button
              className={`nav-link py-2 px-3${tab === t.id ? " active" : ""}`}
              style={{ fontSize: 12.5, cursor: "pointer", border: "none", background: "none", whiteSpace: "nowrap" }}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div style={{
        border: "1px solid #dee2e6", borderTop: "none",
        borderRadius: "0 0 8px 8px",
        maxHeight: 520, overflowY: "auto",
        padding: "18px 20px",
        background: "#fff",
      }}>
        {tab === "claude"   && <ClaudeGuide token={latestToken} />}
        {tab === "chatgpt"  && <ChatGPTGuide token={latestToken} />}
        {tab === "copilot"  && <CopilotStudioGuide token={latestToken} />}
        {tab === "gemini"   && <GeminiGuide token={latestToken} />}
        {tab === "slack"    && <SlackGuide token={latestToken} />}
        {tab === "commands" && <CommandsGuide />}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function McpTokens() {
  const { t } = useTranslation("common");
  const [tokens, setTokens]               = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [isSaving, setIsSaving]           = useState(false);
  const [newName, setNewName]             = useState("");
  const [newTokenValue, setNewTokenValue] = useState(null);
  const [revokeTarget, setRevokeTarget]   = useState(null);
  const [copiedId, setCopiedId]           = useState(null);
  const [error, setError]                 = useState("");
  const [nameError, setNameError]         = useState("");

  // OAuth connect mode — set when page is opened by Claude Desktop via /oauth/authorize
  const [connectParams, setConnectParams] = useState(null); // { state, callback }
  const [isConnecting, setIsConnecting]   = useState(false);
  const [connectError, setConnectError]   = useState("");
  const connectHandledRef                 = useRef(false);

  // Detect OAuth connect mode from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isConnect = params.get("connect") === "true";
    const state     = params.get("state");
    const callback  = params.get("callback");

    if (isConnect && state && callback) {
      if (!getAccessToken()) {
        // New tab opened by claude.ai has empty sessionStorage — save URL and go to login.
        // After login, redirectToHome() in LoginEvent will send the user back here.
        sessionStorage.setItem("mcp_oauth_redirect", window.location.href);
        window.location.replace("/");
        return;
      }
      setConnectParams({ state, callback });
    }
  }, []);

  const fetchTokens = useCallback(async (silent = false) => {
    if (!getAccessToken()) return; // not authenticated yet — skip
    if (!silent) setIsLoading(true);
    setError("");
    try {
      const res  = await fetchWithTokenRefresh2(LARAVEL_BASE_URL + McpTokenList_API, { method: "GET" });
      const json = await res.json();
      setTokens(json?.Data?.Records ?? []);
    } catch {
      setError(t("mcp_token_load_error", "Failed to load tokens. Please try again."));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  // Called when user clicks "Connect" in the OAuth banner
  const handleOAuthConnect = async () => {
    if (!connectParams || connectHandledRef.current) return;
    connectHandledRef.current = true;
    setIsConnecting(true);
    setConnectError("");

    try {
      const tokenName = `Claude Desktop — ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
      const res  = await fetchWithTokenRefresh2(LARAVEL_BASE_URL + McpTokenGenerate_API, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ name: tokenName }),
      });
      const json = await res.json();

      if (json?.Data?.ResponseCode === 200) {
        const mcpToken = json.Data.Records.token;
        // Redirect back to MCP server — it will complete the OAuth flow
        const redirect = new URL(connectParams.callback);
        redirect.searchParams.set("token", mcpToken);
        redirect.searchParams.set("state", connectParams.state);
        window.location.href = redirect.toString();
      } else {
        connectHandledRef.current = false;
        setConnectError(json?.Data?.ErrorMessage || t("mcp_token_create_error", "Failed to create token. Please try again."));
        setIsConnecting(false);
      }
    } catch {
      connectHandledRef.current = false;
      setConnectError(t("mcp_token_create_error", "Connection failed. Please try again."));
      setIsConnecting(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { setNameError(t("mcp_token_name_required", "Token name is required.")); return; }
    setNameError("");
    setIsSaving(true);
    setError("");
    try {
      const res  = await fetchWithTokenRefresh2(LARAVEL_BASE_URL + McpTokenGenerate_API, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ name: newName.trim() }),
      });
      const json = await res.json();
      if (json?.Data?.ResponseCode === 200) {
        setNewTokenValue(json.Data.Records.token);
        setNewName("");
        fetchTokens(true);
      } else {
        setError(json?.Data?.ErrorMessage || t("mcp_token_create_error", "Failed to create token."));
      }
    } catch {
      setError(t("mcp_token_create_error", "Failed to create token."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsSaving(true);
    setError("");
    try {
      const res  = await fetchWithTokenRefresh2(`${LARAVEL_BASE_URL}${McpTokenRevoke_API}/${revokeTarget.token_id}`, { method: "DELETE" });
      const json = await res.json();
      if (json?.Data?.ResponseCode === 200) {
        setTokens((prev) => prev.filter((t) => t.token_id !== revokeTarget.token_id));
      } else {
        setError(json?.Data?.ErrorMessage || t("mcp_token_revoke_error", "Failed to revoke token."));
      }
    } catch {
      setError(t("mcp_token_revoke_error", "Failed to revoke token."));
    } finally {
      setIsSaving(false);
      setRevokeTarget(null);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div>
      <Sidebar />
      <div className="pt-96 my-container active-cont">
        <div className="container">
          <div className="row mt-md-4 mt-85">
            <div className="col-xl-8 offset-xl-2">
    <div className="mcp-tokens-panel">

      {/* OAuth connect banner — shown when Claude Desktop redirects here */}
      {connectParams && (
        <ConnectBanner
          onApprove={handleOAuthConnect}
          isConnecting={isConnecting}
          connectError={connectError}
        />
      )}

      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="text-success">{KEY_ICON}</span>
        <h5 className="mb-0 fw-semibold">{t("mcp_api_tokens", "API / MCP Tokens")}</h5>
      </div>
      <p className="text-muted small mb-4">
        {t("mcp_token_description", "Personal access tokens let AI agents (Claude, ChatGPT) access your Ronspot account through the MCP server. Tokens never expire but can be revoked at any time.")}
      </p>

      {/* Error banner */}
      {error && (
        <div className="alert alert-danger py-2 small mb-3">{error}</div>
      )}

      {/* Generate form */}
      <div className="card border-0 bg-light rounded-3 p-3 mb-4">
        <h6 className="fw-semibold mb-3">{t("mcp_generate_new", "Generate new token")}</h6>
        <form onSubmit={handleGenerate} className="d-flex gap-2 align-items-start">
          <div className="flex-grow-1">
            <input
              type="text"
              className={`form-control form-control-sm${nameError ? " is-invalid" : ""}`}
              placeholder={t("mcp_token_name_placeholder", "Token label (e.g. Claude Desktop – Work)")}
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setNameError(""); }}
              maxLength={80}
              disabled={isSaving}
            />
            {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
          </div>
          <button
            type="submit"
            className="btn btn-success btn-sm px-3 text-nowrap"
            disabled={isSaving || !newName.trim()}
          >
            {isSaving ? t("mcp_generating", "Generating…") : t("mcp_generate_btn", "Generate")}
          </button>
        </form>
      </div>

      {/* One-time token reveal */}
      {newTokenValue && (
        <div className="alert alert-success d-flex align-items-start gap-2 mb-4 small">
          <div className="flex-grow-1">
            <div className="fw-semibold mb-1">{t("mcp_token_created", "Token created — copy it now, it won't be shown again.")}</div>
            <code className="d-block text-break user-select-all bg-white border rounded px-2 py-1">{newTokenValue}</code>
          </div>
          <button
            className="btn btn-sm btn-outline-success text-nowrap"
            onClick={() => copyToClipboard(newTokenValue, "new")}
          >
            {copiedId === "new" ? t("mcp_copied", "Copied!") : <>{COPY_ICON} {t("mcp_copy", "Copy")}</>}
          </button>
          <button className="btn-close ms-1" onClick={() => setNewTokenValue(null)} />
        </div>
      )}

      {/* Token list */}
      <h6 className="fw-semibold mb-2">{t("mcp_active_tokens", "Active tokens")}</h6>
      {isLoading ? (
        <div className="text-center py-4">
          <img src={loadingGIF} alt="Loading" style={{ width: 36 }} />
        </div>
      ) : tokens.length === 0 ? (
        <p className="text-muted small">{t("mcp_no_tokens", "No tokens yet. Generate one above.")}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{t("mcp_col_name", "Name")}</th>
                <th>{t("mcp_col_created", "Created")}</th>
                <th>{t("mcp_col_last_used", "Last used")}</th>
                <th className="text-end">{t("mcp_col_actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((tok) => (
                <tr key={tok.token_id}>
                  <td className="fw-medium">{tok.name}</td>
                  <td className="text-muted small">{formatDate(tok.created_at)}</td>
                  <td className="text-muted small">{tok.last_used ? formatDate(tok.last_used) : t("mcp_never_used", "Never")}</td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setRevokeTarget(tok)}
                      title={t("mcp_revoke_btn", "Revoke")}
                    >
                      {TRASH_ICON}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Guide */}
      <McpUserGuide latestToken={newTokenValue} />

      {/* Revoke confirmation modal */}
      {revokeTarget && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h6 className="modal-title fw-semibold">{t("mcp_revoke_confirm_title", "Revoke token?")}</h6>
                  <button className="btn-close" onClick={() => setRevokeTarget(null)} />
                </div>
                <div className="modal-body small">
                  {t("mcp_revoke_confirm_body", "This will permanently revoke")} <strong>{revokeTarget.name}</strong>. {t("mcp_revoke_confirm_note", "Any MCP agent using it will lose access immediately.")}
                </div>
                <div className="modal-footer border-0 pt-0 gap-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setRevokeTarget(null)}>
                    {t("mcp_cancel", "Cancel")}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={handleRevoke} disabled={isSaving}>
                    {isSaving ? t("mcp_revoking", "Revoking…") : t("mcp_revoke_btn", "Revoke")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
        </>
      )}
    </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
